import { useState } from "react";
import { MessageSquare, Phone, Instagram, MapPin, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { treatments } from "@/data/treatments";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Book() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", treatment: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, form);
      toast.success("Thank you — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", treatment: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or reach us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const contacts = [
    { icon: MessageSquare, label: "SMS", value: "(360) 820-4854", href: "sms:+13608204854", testid: "contact-sms" },
    { icon: Phone, label: "Phone", value: "(360) 820-4854", href: "tel:+13608204854", testid: "contact-phone" },
    { icon: Instagram, label: "Instagram", value: "@ne_cosmetologist", href: "https://www.instagram.com/ne_cosmetologist", testid: "contact-instagram" },
    { icon: MapPin, label: "Location", value: "12040 98th Ave NE, Unit 200, Kirkland, WA 98034", href: "https://maps.google.com/?q=12040+98th+Ave+NE+Unit+200+Kirkland+WA+98034", testid: "contact-maps" }
  ];

  return (
    <div data-testid="book-page" className="pt-32 md:pt-40 bg-[#FAF8F5]">
      <SEO
        title="Book a Consultation | Natalia Medical Aesthetics, Kirkland WA"
        description="Book your private consultation with Natalia Medical Aesthetics in Kirkland, WA. Call (360) 820-4854 or request online."
        path="/book"
      />
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-4xl">
          <div className="overline mb-6">Book Consultation</div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-tight">
            A private conversation, <em className="italic text-[#8A847C]">at your pace.</em>
          </h1>
          <p className="mt-10 text-[1.05rem] text-[#5C5C5C] leading-relaxed max-w-2xl">
            Send us a note or reach out directly. We reply personally within one
            business day.
          </p>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Contact info */}
          <div className="lg:col-span-5">
            <div className="overline mb-6">Reach us</div>
            <div className="space-y-3">
              {contacts.map((c) => (
                <a
                  key={c.label}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={c.testid}
                  className="group flex items-center justify-between bg-white border border-[#E5E0DA] rounded-[24px] px-7 py-6 hover:shadow-[0_12px_48px_rgba(0,0,0,0.05)] transition-all"
                >
                  <div className="flex items-center gap-5">
                    <div className="w-11 h-11 rounded-full bg-[#F3EFEA] flex items-center justify-center">
                      <c.icon strokeWidth={1.25} className="w-5 h-5 text-[#1A1A1A]" />
                    </div>
                    <div>
                      <div className="overline mb-1">{c.label}</div>
                      <div className="text-[#1A1A1A] text-base font-medium">{c.value}</div>
                    </div>
                  </div>
                  <ArrowUpRight strokeWidth={1.5} className="w-4 h-4 text-[#8A847C] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </a>
              ))}
            </div>

            <div className="mt-10 rounded-[28px] overflow-hidden border border-[#E5E0DA] aspect-[16/10] bg-[#F3EFEA]">
              <iframe
                data-testid="google-map"
                title="Natalia Medical Aesthetics Location"
                src="https://www.google.com/maps?q=12040+98th+Ave+NE+Unit+200+Kirkland+WA+98034&output=embed"
                className="w-full h-full grayscale-[30%]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className="mt-10">
              <div className="overline mb-4">Hours</div>
              <ul className="text-[#5C5C5C] space-y-2 text-sm">
                <li className="flex justify-between border-b border-[#E5E0DA] pb-2"><span>Monday – Friday</span><span>9:00 AM – 6:00 PM</span></li>
              </ul>
              <p className="text-xs text-[#8A847C] mt-3">By appointment only</p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              data-testid="consultation-form"
              onSubmit={submit}
              className="bg-white border border-[#E5E0DA] rounded-[28px] p-8 md:p-14"
            >
              <div className="overline mb-4">Request a consultation</div>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">Send us a note.</h2>
              <p className="mt-4 text-[#5C5C5C]">Every enquiry is answered personally within one business day.</p>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
                <div>
                  <label className="overline block mb-1">Full Name</label>
                  <input
                    data-testid="form-name"
                    type="text"
                    className="luxury-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="overline block mb-1">Email</label>
                  <input
                    data-testid="form-email"
                    type="email"
                    className="luxury-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <label className="overline block mb-1">Phone</label>
                  <input
                    data-testid="form-phone"
                    type="tel"
                    className="luxury-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1 (___) ___ ____"
                  />
                </div>
                <div>
                  <label className="overline block mb-1">Treatment of interest</label>
                  <select
                    data-testid="form-treatment"
                    className="luxury-select"
                    value={form.treatment}
                    onChange={(e) => setForm({ ...form, treatment: e.target.value })}
                  >
                    <option value="">Not sure yet</option>
                    {treatments.map((t) => (
                      <option key={t.slug} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2 mt-4">
                  <label className="overline block mb-1">Tell us a little about your skin goals</label>
                  <textarea
                    data-testid="form-message"
                    className="luxury-input resize-none"
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="What would you like to address?"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="form-submit"
                disabled={submitting}
                className="btn-primary mt-12 disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Request Consultation"}
              </button>
              <p className="mt-4 text-xs text-[#8A847C]">By submitting, you agree to be contacted by our team. We never share your information.</p>
            </form>
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 pb-24 md:pb-32">
        <div className="bg-[#1A1A1A] text-white rounded-[28px] p-12 md:p-24 text-center">
          <div className="overline !text-[#8A847C] mb-8">Prefer to talk?</div>
          <h3 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl mx-auto">
            Message us on <em className="italic">Instagram</em> or send an SMS.
          </h3>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <a href="sms:+13608204854" data-testid="cta-sms" className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] rounded-full px-10 py-5 text-[0.85rem] font-medium font-medium hover:bg-[#F3EFEA] transition-colors">
              <MessageSquare strokeWidth={1.5} className="w-4 h-4" /> Send SMS
            </a>
            <a href="https://www.instagram.com/ne_cosmetologist" target="_blank" rel="noreferrer" data-testid="cta-ig" className="inline-flex items-center gap-2 bg-transparent border border-white text-white rounded-full px-10 py-5 text-[0.85rem] font-medium font-medium hover:bg-white hover:text-[#1A1A1A] transition-colors">
              <Instagram strokeWidth={1.5} className="w-4 h-4" /> Instagram
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
